import React, {
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { Switch, Route, BrowserRouter as Router } from 'react-router-dom';
import { Shell, getMenuItemsRecursively } from 'unity-fluent-library';
import PageLoading from './UI/routing/PageLoading';
import ErrorBoundary from './ErrorBoundary';
import { FleetRoutes } from './FleetIntegration';
import { useAxiosGet } from './utils/useAxiosGet';
import { useUser, useIsUnityAuthenticated } from './utils/auth';
import { SnackbarProvider } from 'notistack';
import UserWrapper from './appBarContent/UserWrapper';
import AppBarControls from './appBarContent/AppBarControls';
import { mockMenus } from './mockData/mockData';
import AadCallback from './routes/callback/AadCallback';
import { useUserActions } from './utils/auth/users';
import { useTheme } from '@material-ui/core';
import LoadingIndicator from './utils/auth/LoadingIndicator';
import ShellWrapper from './ShellWrapper';
import { ShellContext } from './contexts/ShellContext';
import PrivateRoute from './routes/PrivateRoute';

const PageNotFound = lazy(() => import('./UI/routing/PageNotFoundRoute'));
const PageForbidden = lazy(() => import('./UI/routing/PageForbiddenRoute.js'));
const LocalLogin = lazy(() => import('./routes/auth/LocalLogin'));

const IS_LOCAL_AUTH = process.env.REACT_APP_LOCAL_AUTH === 'true';

const Routes = () => {
  const user = useUser();
  const theme = useTheme();
  const isAuthenticated = useIsUnityAuthenticated();
  const [menuToggle, setMenuToggle] = useState(true);
  const [inquiryMenuToggle, setInquiryMenuToggle] = useState(true);
  const [headerExpander, setHeaderExpander] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const [secondExtendedWindow, setSecondExtendedWindow] = useState(false);
  const [showToggle1, setShowToggle1] = useState(false);
  const [showToggle2, setShowToggle2] = useState(false);
  const [primaryExpanded, setPrimaryExpanded] = useState(false);
  const [secondaryExpanded, setSecondaryExpanded] = useState(true);
  const { setUser } = useUserActions();
  const [userData, setUserData] = useState(user);

  const useMockData = process.env.REACT_APP_USE_MOCKS === 'true';

  const LEFT_MENU_ID = 1; // Left Menu
  // FIXME: THIS WILL NEED TO BE REMOVED AND REPLACED WITH THE CONTEXT ONCE TENANT SWITCHING IS AVAILABLE
  const FLEET_TENANT_ID = '8727dfbf-6170-4696-aa1d-222aba1832c3'; // TerraSpatial

  // Left menu
  const [{ data: leftMenu }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `menus?menuTypeId=${LEFT_MENU_ID}&productId=${process.env.REACT_APP_UNITY_PRODUCT_ID}&tenantId=${FLEET_TENANT_ID}`,
    {},
    !!!user?.currentTenantId
  );

  const [
    { data: currentTenant, error: getTenantError },
    refetchCurrentTenant,
  ] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `tenants/${user?.currentTenantId}`,
    {},
    false
  );

  const [{ data: userTenants }, refetchUserTenants] = useAxiosGet(
    process.env.REACT_APP_SECURITY_API_BASE,
    `users/${user ? user?.id : ''}/unitytenants`,
    {},
    !!!user?.id
  );

  // App, Builder, and Favorites menus
  const [{ data: appSelectorMenus }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `menus?tenantId=${user?.currentTenantId}&verticalId=${
      process.env.REACT_APP_UNITY_VERTICAL_ID
    }&userId=${user ? user?.id : ''}`,
    {},
    !!!user?.id
  );

  const userHasAccessToTenant = useMemo(() => {
    if (user?.tenantIds?.includes(user?.currentTenantId)) {
      return true;
    }
    return false;
  }, [user]);

  const renderRoutes = useMemo(() => {
    return (
      <Switch>
        <Route exact path="/aad_callback" component={AadCallback} />
        {/* If you have different types of login based on .env */}
        {!isAuthenticated && IS_LOCAL_AUTH && (
          <Route path="/login" exact component={LocalLogin} />
        )}
        <FleetRoutes />
      </Switch>
    );
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    setUserData(user);
  }, [user, setUserData]);

  useEffect(() => {
    if (isAuthenticated && user?.currentTenantId) {
      refetchCurrentTenant().then(tenant => {
        const currentTenantTheme = tenant.data.theme;
        if (theme.setCurrentTheme && currentTenantTheme !== undefined) {
          theme.setCurrentTheme(currentTenantTheme);
        }
      });
    }
  }, [refetchCurrentTenant, theme, isAuthenticated, user]);

  if (
    (!theme.isInitialized && isAuthenticated && !getTenantError) ||
    (isAuthenticated && user?.statusCode !== 200)
  ) {
    return <LoadingIndicator />;
  }

  if (!user) return null;

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <SnackbarProvider>
        <ShellContext.Provider
          value={{
            menuToggle,
            setMenuToggle,
            headerExpander,
            setHeaderExpander,
            headerTitle,
            setHeaderTitle,
            secondExtendedWindow,
            setSecondExtendedWindow,
            showToggle1,
            setShowToggle1,
            showToggle2,
            setShowToggle2,
            primaryExpanded,
            setPrimaryExpanded,
            secondaryExpanded,
            setSecondaryExpanded,
            inquiryMenuToggle,
            setInquiryMenuToggle,
          }}
        >
          <ShellWrapper
            userAvatar={
              <UserWrapper
                tenant={{
                  label: 'Current Tenant',
                  current: currentTenant,
                  options: userTenants,
                  onSelect: setUser,
                }}
                unityUrl={process.env.REACT_APP_UNITY_URL}
                user={user}
                userHasAccessToTenant={userHasAccessToTenant}
              />
            }
            appContent={<AppBarControls />}
            leftMenu={userHasAccessToTenant ? leftMenu : []}
            appSelectorMenus={userHasAccessToTenant ? appSelectorMenus : []}
            user={user}
            navRoot={'Home'}
            siteName="Fleet Management"
            menuToggle={menuToggle}
            inquiryMenuToggle={inquiryMenuToggle}
            setInquiryMenuToggle={setInquiryMenuToggle}
            headerExpander={headerExpander}
            secondExtendedWindow={secondExtendedWindow}
            showToggle1={showToggle1}
            showToggle2={showToggle2}
            primaryExpanded={primaryExpanded}
            secondaryExpanded={secondaryExpanded}
            setPrimaryExpanded={setPrimaryExpanded}
            setSecondaryExpanded={setSecondaryExpanded}
            useMockData={useMockData}
            mockMenus={mockMenus}
            unityUrl={process.env.REACT_APP_UNITY_URL}
            hideLayout={!isAuthenticated && IS_LOCAL_AUTH}
          >
            <ErrorBoundary>
              <Suspense fallback={<PageLoading />}>{renderRoutes}</Suspense>
            </ErrorBoundary>
          </ShellWrapper>
        </ShellContext.Provider>
      </SnackbarProvider>
    </Router>
  );
};
export default Routes;
